import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EventWithScenarios,
  ScenarioWithRoomsAndStages,
  ScenarioWithStagesAndLimitedRooms,
} from '../../../../common/types';
import { StageGraph } from '../../../../components/stages/StageGraph';
import { ShareButton } from '../../../../components/ShareButton';
import { CreateEventModal } from '../create';
import { CopyEventScenarioModal, CreateEventScenarioModal } from './create';
import { BackButton } from '../../../../components/BackButton';
import { BreadcrumbGroup } from '@cloudscape-design/components';

export type EventViewProps = {
  event: EventWithScenarios;
  refetch: () => void;
};

export function EventView(props: EventViewProps) {
  const { event, refetch } = props;
  const [createOpen, setCreateOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div>
      {/* Event Title */}
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold mb-8">{event.name}</h1>
        <button
          className="bg-white text-black py-2 px-4 rounded hover:bg-gray-200 transition-colors duration-300"
          onClick={(e) => {
            e.preventDefault();
            setCreateOpen(true);
          }}
        >
          Create Scenario
        </button>
      </div>

      {/* Grid Layout for Scenarios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {event.scenarios.map((scenario) => (
          <ScenarioCard
            key={scenario.scenarioUuid}
            scenario={scenario}
            event={event}
            refetch={refetch}
          />
        ))}
      </div>
      <CreateEventScenarioModal
        eventUuid={event.eventUuid}
        open={createOpen}
        setOpen={setCreateOpen}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
}

type ScenarioCardProps = {
  scenario: ScenarioWithStagesAndLimitedRooms;
  event: EventWithScenarios;
  refetch: () => void;
};

function ScenarioCard(props: ScenarioCardProps) {
  const { scenario, event, refetch } = props;
  const [copyOpen, setCopyOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div key={scenario.scenarioUuid} className="bg-[#1e2a38] p-6 rounded-lg shadow-md">
      {/* Scenario Image */}
      <div className="mb-4">
        <img
          src={scenario.image}
          alt={`${scenario.name} scenario image`}
          className="w-full h-48 object-cover rounded-lg"
        />
      </div>

      {/* Scenario Info */}
      <div className="mb-4">
        <div className="flex flex-col justify-end space-y-4">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-4xl font-bold">{scenario.name}</h1>
          </div>
          <div className="flex items-left gap-4">
            <ShareButton scenarioUuid={scenario.scenarioUuid} />
            <button
              className="bg-white text-black py-2 px-4 rounded hover:bg-gray-200 transition-colors duration-300"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/manage/scenarios/${scenario.scenarioUuid}`);
              }}
            >
              Edit
            </button>
            <button
              className="bg-white text-black py-2 px-4 rounded hover:bg-gray-200 transition-colors duration-300"
              onClick={(e) => {
                e.preventDefault();
                setCopyOpen(true);
              }}
            >
              Copy
            </button>
          </div>
        </div>
        <div>
          <p className="text-gray-400 text-sm mb-2 mt-2">
            Difficulty: {scenario.difficulty[0].toUpperCase() + scenario.difficulty.slice(1)} |
            Rooms: {scenario.rooms.length}
          </p>
        </div>
      </div>
      {/* Stage Graph (with stages and puzzles) */}
      {scenario.stages.length > 0 && (
        <div className="mb-4">
          <StageGraph stages={scenario.stages} />
        </div>
      )}
      <CopyEventScenarioModal
        scenarioUuid={scenario.scenarioUuid}
        open={copyOpen}
        setOpen={setCopyOpen}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
}
